import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as PortOne from '@portone/browser-sdk/v2';
import { format } from 'date-fns';
import {
  FiChevronLeft, FiDollarSign, FiCreditCard,
  FiInfo, FiRefreshCw, FiClock, FiCheck
} from 'react-icons/fi';
import api from '../api';
import { PORTONE_CONFIG } from '../config/portone';

export default function PointsCharge() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [impCode, setImpCode] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CARD'); // 결제 수단

  // 기존 계좌이체 방식
  const [showLegacyMode, setShowLegacyMode] = useState(false);
  const [chargeForm, setChargeForm] = useState({
    amount: '',
    depositorName: '',
    depositedAt: format(new Date(), 'yyyy-MM-dd'),
    note: ''
  });
  const [chargeRequests, setChargeRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  // 빠른 충전 금액
  const quickAmounts = [
    { value: 10000, label: '1만원' },
    { value: 30000, label: '3만원' },
    { value: 50000, label: '5만원' },
    { value: 100000, label: '10만원' },
  ];

  useEffect(() => {
    loadImpCode();
    if (showLegacyMode) {
      loadChargeRequests();
    }
  }, [showLegacyMode]);

  const loadImpCode = async () => {
    try {
      const res = await api.get('/api/payments/store-id');
      setImpCode(res.data.storeId);
    } catch (error) {
      console.error('Failed to load store ID:', error);
      alert('결제 시스템을 불러오는데 실패했습니다.');
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountSubmit = () => {
    const amount = parseInt(customAmount);
    if (!amount || amount < 1000) {
      alert('최소 충전 금액은 1,000원입니다.');
      return;
    }
    if (amount > 10000000) {
      alert('최대 충전 금액은 10,000,000원입니다.');
      return;
    }
    setSelectedAmount(amount);
  };

  const handlePayment = async () => {
    if (!selectedAmount) {
      alert('충전 금액을 선택해주세요.');
      return;
    }

    try {
      setLoading(true);

      // 결제 준비 (Payment 문서 생성)
      const prepareRes = await api.post('/api/payments/prepare', {
        amount: selectedAmount,
        purpose: 'point_charge'
      });

      const { merchant_uid, amount, name, customer } = prepareRes.data;

      // 아임포트 V2 결제 요청
      const response = await PortOne.requestPayment({
        storeId: PORTONE_CONFIG.storeId,
        paymentId: merchant_uid, // 우리의 주문번호를 paymentId로 사용
        orderName: name,
        totalAmount: amount,
        currency: 'CURRENCY_KRW',
        channelKey: PORTONE_CONFIG.channelKey,
        payMethod: paymentMethod, // 선택한 결제 수단
        customer: {
          fullName: customer.name,
          email: customer.email,
          phoneNumber: customer.phone,
        },
      });

      if (response.code != null) {
        // 결제 실패
        const failUrl = `/payments/fail?code=${encodeURIComponent(response.code)}&message=${encodeURIComponent(response.message || '결제에 실패했습니다.')}`;
        navigate(failUrl);
        return;
      }

      // 결제 성공 - 서버에서 검증
      navigate('/payments/success?imp_uid=' + response.paymentId + '&merchant_uid=' + merchant_uid + '&amount=' + amount);

    } catch (error) {
      console.error('Payment request error:', error);
      alert(error.response?.data?.error || '결제 요청에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 기존 계좌이체 방식 (Legacy)
  const loadChargeRequests = async () => {
    try {
      setLoadingRequests(true);
      const res = await api.get('/api/deposits/requests');
      setChargeRequests(res.data.requests || []);
    } catch (error) {
      console.error('Failed to load charge requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleLegacyChargeRequest = async (e) => {
    e.preventDefault();

    if (!chargeForm.amount || !chargeForm.depositorName) {
      alert('금액과 입금자명을 입력해주세요.');
      return;
    }

    if (parseFloat(chargeForm.amount) <= 0) {
      alert('금액은 0보다 커야 합니다.');
      return;
    }

    if (!window.confirm(
      `충전 요청을 제출하시겠습니까?\n\n` +
      `입금액: ${parseInt(chargeForm.amount).toLocaleString()}원\n` +
      `입금자명: ${chargeForm.depositorName}\n\n` +
      `※ 실제 입금 확인 후 포인트가 충전됩니다.`
    )) {
      return;
    }

    try {
      setLoading(true);
      const res = await api.post('/api/deposits/request', {
        amount: parseFloat(chargeForm.amount),
        depositorName: chargeForm.depositorName,
        depositedAt: chargeForm.depositedAt,
        note: chargeForm.note
      });

      setResultMessage(res.data.message || '충전 요청이 접수되었습니다.\n입금 확인 후 포인트가 충전됩니다.');
      setShowResultModal(true);

      setChargeForm({
        amount: '',
        depositorName: '',
        depositedAt: format(new Date(), 'yyyy-MM-dd'),
        note: ''
      });

      loadChargeRequests();
    } catch (error) {
      console.error('Charge request error:', error);
      alert(error.response?.data?.error || '충전 요청에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning">확인중</span>;
      case 'approved':
        return <span className="badge badge-success">완료</span>;
      case 'rejected':
        return <span className="badge badge-danger">반려</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  return (
    <div className="points-charge-page">
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/points')}>
            <FiChevronLeft />
          </button>
          <div>
            <h2>포인트 충전</h2>
            <p>간편하고 안전한 온라인 결제</p>
          </div>
        </div>
      </div>

      {!showLegacyMode ? (
        <>
          {/* 충전 금액 선택 */}
          <div className="charge-section">
            <div className="charge-card">
              <h3><FiDollarSign /> 충전 금액 선택</h3>
              <p className="description">충전하실 금액을 선택하거나 직접 입력하세요</p>

              {/* 빠른 선택 */}
              <div className="quick-amounts">
                {quickAmounts.map((item) => (
                  <button
                    key={item.value}
                    className={`amount-btn ${selectedAmount === item.value ? 'active' : ''}`}
                    onClick={() => handleAmountSelect(item.value)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* 직접 입력 */}
              <div className="custom-amount">
                <label>직접 입력</label>
                <div className="input-group">
                  <input
                    type="number"
                    placeholder="최소 1,000원"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCustomAmountSubmit();
                      }
                    }}
                  />
                  <button
                    className="btn-secondary"
                    onClick={handleCustomAmountSubmit}
                    disabled={!customAmount}
                  >
                    적용
                  </button>
                </div>
                {customAmount && (
                  <span className="amount-display">
                    {parseInt(customAmount).toLocaleString()}원 = {parseInt(customAmount).toLocaleString()}P
                  </span>
                )}
              </div>

              {/* 선택된 금액 표시 */}
              {selectedAmount > 0 && (
                <div className="selected-amount-display">
                  <div className="amount-info">
                    <span className="label">충전 금액</span>
                    <span className="value">{selectedAmount.toLocaleString()}원</span>
                  </div>
                  <div className="amount-info">
                    <span className="label">충전 포인트</span>
                    <span className="value highlight">{selectedAmount.toLocaleString()}P</span>
                  </div>
                </div>
              )}


              {/* 결제 버튼 */}
              {selectedAmount > 0 && (
                <button
                  className="btn-primary btn-block btn-lg"
                  onClick={handlePayment}
                  disabled={loading}
                  style={{ marginTop: '24px' }}
                >
                  <FiCreditCard />
                  {loading ? '처리 중...' : `${selectedAmount.toLocaleString()}원 결제하기`}
                </button>
              )}
            </div>
          </div>

          {/* 안내 사항 */}
          <div className="info-box" style={{ marginTop: '24px' }}>
            <FiInfo />
            <div>
              <h4>안전한 결제</h4>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>포트원(아임포트)의 안전한 결제 시스템을 이용합니다</li>
                <li>결제 완료 즉시 포인트가 자동 충전됩니다</li>
                <li>카드, 가상계좌, 간편결제 등 다양한 결제수단을 지원합니다</li>
                <li>결제 내역은 포인트 충전 기록에서 확인하실 수 있습니다</li>
              </ul>
            </div>
          </div>

          {/* 기존 방식으로 충전하기 */}
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button
              className="btn-text"
              onClick={() => setShowLegacyMode(true)}
              style={{ color: '#666', textDecoration: 'underline' }}
            >
              계좌이체로 충전하기 (기존 방식)
            </button>
          </div>
        </>
      ) : (
        <>
          {/* 기존 계좌이체 방식 */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <button
              className="btn-secondary"
              onClick={() => setShowLegacyMode(false)}
            >
              <FiChevronLeft /> 온라인 결제로 돌아가기
            </button>
          </div>

          <div className="info-box" style={{ marginBottom: '24px', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
            <FiInfo color="#3B82F6" />
            <div>
              <h4 style={{ color: '#1E40AF' }}>계좌이체 충전 방법</h4>
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#1E40AF' }}>
                <li>아래 계좌로 원하시는 금액을 입금해주세요</li>
                <li>입금 후 아래 양식에 입금 정보를 입력해주세요</li>
                <li>입금 확인 후 포인트가 자동으로 충전됩니다</li>
              </ol>
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#1E40AF' }}>
                  입금 계좌: 국민은행 123-456-789012 (주)에듀스태프
                </p>
              </div>
            </div>
          </div>

          <div className="charge-section">
            <div className="charge-card">
              <h3><FiCreditCard /> 충전 요청</h3>
              <p className="description">입금하신 정보를 입력해주세요</p>

              <form onSubmit={handleLegacyChargeRequest}>
                <div className="form-group">
                  <label>입금 금액 *</label>
                  <input
                    type="number"
                    placeholder="10000"
                    value={chargeForm.amount}
                    onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
                    required
                  />
                  {chargeForm.amount && (
                    <span className="amount-display">
                      {parseInt(chargeForm.amount).toLocaleString()}원 = {parseInt(chargeForm.amount).toLocaleString()}P
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>입금자명 *</label>
                  <input
                    type="text"
                    placeholder="입금하신 분의 이름"
                    value={chargeForm.depositorName}
                    onChange={(e) => setChargeForm({ ...chargeForm, depositorName: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>입금 날짜 *</label>
                  <input
                    type="date"
                    value={chargeForm.depositedAt}
                    onChange={(e) => setChargeForm({ ...chargeForm, depositedAt: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>메모 (선택)</label>
                  <textarea
                    placeholder="추가 전달사항이 있으면 입력하세요"
                    value={chargeForm.note}
                    onChange={(e) => setChargeForm({ ...chargeForm, note: e.target.value })}
                    rows={2}
                  />
                </div>

                <button type="submit" className="btn-primary btn-block" disabled={loading}>
                  <FiDollarSign />
                  {loading ? '처리 중...' : '충전 요청'}
                </button>
              </form>
            </div>
          </div>

          {/* 충전 요청 내역 */}
          <div className="charge-history" style={{ marginTop: '32px' }}>
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3>충전 요청 내역</h3>
              <button className="btn btn-sm btn-secondary" onClick={loadChargeRequests} disabled={loadingRequests}>
                <FiRefreshCw /> 새로고침
              </button>
            </div>

            {loadingRequests ? (
              <div className="loading">로딩 중...</div>
            ) : chargeRequests.length === 0 ? (
              <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <FiClock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p>충전 요청 내역이 없습니다.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>요청일</th>
                    <th>입금자명</th>
                    <th>금액</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {chargeRequests.map((req) => (
                    <tr key={req._id}>
                      <td>{format(new Date(req.createdAt), 'MM/dd HH:mm')}</td>
                      <td>{req.depositorName}</td>
                      <td>{req.amount?.toLocaleString()}원</td>
                      <td>{getStatusBadge(req.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* 결과 모달 */}
      {showResultModal && (
        <div className="modal-overlay" onClick={() => setShowResultModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header success">
              <FiCheck size={48} />
              <h3>요청 접수 완료</h3>
            </div>
            <div className="modal-body">
              <p style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>{resultMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary btn-block" onClick={() => setShowResultModal(false)}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
